import { LitElement, html, css } from 'lit'
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js'
import $ from '../../lib/quantdom'
import { debounce } from '../../lib/utils'
import { get } from '../../lib/ajax'
import { dropZeros, resizeImageSrc } from '../../lib/utils'

export class SearchSuggest extends LitElement {
  static styles = [
    css`
      /* hide until we make full search page */
      .view-all {
        display: none;
      }
      .search-suggest {
        height: 100%;
        max-height: 100%;
        width: 100%;
        overflow-y: auto;
      }

      .search-suggest header {
        padding: 0 20px;
        display: flex;
        justify-content: space-between;
        border-bottom: 1px solid #e5e5e5;
      }

      .products {
        display: flex;
        flex-direction: column;
        gap: 15px;
        padding: 0 20px;
        overflow-y: auto;
      }

      .product {
        display: flex;
        padding: 10px;
        align-items: center;
        gap: 12px;
      }

      .product {
        text-decoration: none;
        color: #000;
      }

      .product > img {
        width: 100%;
        height: auto;
        max-width: 80px;
      }

      :host {
        display: block;
      }

      .main {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
        overflow-y: auto;
      }
      .product__details {
        font-size: 14px;
      }

      .product-title {
        margin: 0px;
        font-size: 14px;
        font-weight: 400;
      }

      .top {
        display: flex;
        flex-direction: column;
        height: 100%;
        overflow: hidden;
        width: 100%;
      }
      .bottom {
        display: flex;
        background: #f6f6f6;
        padding: 20px;
        font-size: 16px;
        align-items: center;
        justify-content: space-between;
      }

      .bottom .btn {
        background: #fff;
        padding: 7px 11px;
        color: #000;
        border-radius: 6px;
        text-decoration: none;
        display: flex;
        align-items: center;
        font-size: 13px;
        gap: 5px;
        justify-content: center;
      }

      .live-region-announcer {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border-width: 0;
      }
    `,
  ]

  static properties = {
    for: { type: String },
    open: { type: Boolean, reflect: true },
    searchPageDisabled: { type: Boolean, attribute: 'search-page-disabled' },
    products: { type: Array },
    query: { type: String },
    mobTopEl: { type: String, attribute: 'mobile-top-element' },
  }

  connectedCallback() {
    super.connectedCallback()
    this.open = this.open ?? false
    this.search = $(`input#${this.for}`)
    this.form = $(`form.js-search-form`)
    this.query = ''
    this.products = []
    this.top = this.top ?? null
    this.searchPageDisabled = this.searchPageDisabled ?? false
    // this.settings = window?.Store?.settings?.search
    // this.helpCenter = this.settings?.help_center ?? []
    // this.trendingProducts = this.settings?.trending_products ?? []
    // this.popularSearches = this.settings?.popular_searches ?? []

    //handles clicking on window to close search dropdown
    this.windowClickHandler = (e) => {
      let searchDropdown = this
      if (
        !(searchDropdown == e.target || searchDropdown.contains(e.target)) &&
        !e.target.className.includes('js-search-submit') &&
        !e.target.className.includes('js-search-input') &&
        !e.target.className.includes('js-search-form')
      ) {
        this._close()
      }
    }

    //handles typing in search input
    this.keyupHandler = () => {
      debounce(() => {
        this.query = this.search.val().trim()
        if (this.query) {
          const url = `/search/suggest.json?q=${this.query}&resources[type]=product&resources[limit]=10&options[fields]=title,variants.title,product_type,tag,body`
          get(url).then((res) => {
            this.products = res.resources.results.products
          })
        } else {
          this.products = []
        }
      }, 100)
    }

    this.form.on('submit', (e) => {
      return this.searchPageDisabled ? e.preventDefault() : true
    })

    // when input is in focus, start listening to typing & window clicks
    this.search.on('focus', () => {
      this.open = true
      this.search.on('keyup', this.keyupHandler)
      $(window).on('click', this.windowClickHandler)
    })

    // stop listening to typing in search input when its not in focus
    this.search.on('blur', () => {
      this.search.off('keyup', this.keyupHandler)
    })

    this._setTop()
  }

  //renders results and defaults trending products
  renderProducts() {
    // let results = this.products.length > 0 ? this.products : this.trendingProducts
    let results = this.products.length > 0 ? this.products : []
    return html`${results.map((product) => {
      let img = product.featured_image.url
        ? product.featured_image
        : { url: product.image, alt: product.title, width: '', height: '' }

      return html`
        <a role="group" aria-label="search result product" class="product" href=${product.url}>
          <img
            alt=${img.alt}
            width=${img.width}
            height=${img.height}
            src="${resizeImageSrc(img.url, 250)}"
            srcset="
              ${resizeImageSrc(img.url, 150)} 150w,
              ${resizeImageSrc(img.url, 200)} 200w,
              ${resizeImageSrc(img.url, 300)} 300w
            "
          />
          <div class="product__details">
            <h6 class="product-title">${unsafeHTML(product.title)}</h6>
          </div>
        </a>
      `
    })}`
  }

  // renders results header based of if there are results or not
  renderResultsHeader() {
    if (this.query && this.products.length > 0) {
      return html`Results for "${this.query}"`
    } else if (this.query && this.products.length === 0) {
      // return html`No results for "${this.query}". Trending products:`
      return html`No results for "${this.query}"`
    } else {
      // return html`Trending Products`
      return html``
    }
  }

  render() {
    return html`
      <div class="search-suggest" aria-expanded=${this.open} style="${this.top ? `height: calc(100% - ${this.top});` : ''}">
        <div class="top">
          <div class="main">
            <header>
              <div aria-live="assertive" class="live-region-announcer">
                ${(this.products.length == 0 ? 'no' : this.products.length) + ' results available'}
              </div>
              <h5>${this.renderResultsHeader()}</h5>
              <a
                class="view-all"
                href="/search/?q=${this.query}"
                aria-hidden="${this.products.length === 0 ? 'true' : 'false'}"
              >
                View All
              </a>
            </header>
            <div class="products">${this.renderProducts()}</div>
          </div>
          <div class="bottom">
            <a class="btn" href="/pages/contact">Contact Us</a>
            <a class="btn" href="/pages/store-locations">Find In Stores</a>
            <a class="btn" href="/pages/about-us">About Us</a>
          </div>
        </div>
      </div>
    `
  }

  //closes the search dropdown & removes listener for window clicks for performance
  _close() {
    this.open = false
    this.search.val('')
    $(window).off('click', this.windowClickHandler)
  }

  _setTop() {
    if (this.mobTopEl && this.mobTopEl && window.innerWidth <= 1024) {
      const el = document.querySelector(this.mobTopEl)
      const height = el.offsetHeight
      this.top = height + 'px'
    }
  }
}
customElements.define('search-suggest', SearchSuggest)
